<template>
  <!-- Email preview content -->
  <div class="p-4 bg-white border-[1px] rounded-lg border-gray-200 text-sm">
    <div class="flex items-center">
      <Mail class="w-5 h-5 mr-2" />
      <h2 class="w-full flex-grow text-lg">Referral Email Preview</h2>
    </div>

    <div class="mt-2" :class="{ 'opacity-50': isSending }">
      <!-- Email Preview -->
      <div v-if="applicationsStore.emailPreview">
        <!-- Email Details -->
        <div class="space-y-2">
          <div class="flex items-start gap-2">
            <span class="font-semibold w-20">To:</span>
            <span>{{ applicationsStore.emailPreview.to_email }}</span>
            <span>({{ applicationsStore.emailPreview.to_name }})</span>
          </div>
          <div class="flex items-start gap-2">
            <span class="font-semibold w-20">Subject:</span>
            <span>{{ applicationsStore.emailPreview.subject }}</span>
          </div>

          <div class="flex items-start gap-2" v-if="applicationsStore.emailPreview.cc_list.length">
            <span class="font-semibold w-20">CC:</span>
            <span>{{ applicationsStore.emailPreview.cc_list.join(', ') }}</span>
          </div>
        </div>

        <div
          v-if="
            applicationsStore.emailPreview.employer_details?.outtalent_internal_notes ||
            applicationsStore.emailPreview.referrer_details?.notes
          "
          class="mt-4 border-[1px] rounded-md p-4 bg-green-50 border-green-200 space-y-4"
        >
          <div v-if="applicationsStore.emailPreview.employer_details?.outtalent_internal_notes">
            <h2 class="font-semibold flex items-center text-green-800">
              <CircleAlert class="w-4 h-4 mr-2" />
              Internal Notes for {{ applicationsStore.emailPreview.employer_details?.name }}
            </h2>
            <p class="ml-6">
              {{ applicationsStore.emailPreview.employer_details?.outtalent_internal_notes }}
            </p>
          </div>
          <div v-if="applicationsStore.emailPreview.referrer_details?.notes">
            <h2 class="font-semibold flex items-center text-green-800">
              <CircleAlert class="w-4 h-4 mr-2" />
              Internal Notes for {{ applicationsStore.emailPreview.referrer_details?.name }}
            </h2>
            <p class="ml-6">{{ applicationsStore.emailPreview.referrer_details?.notes }}</p>
          </div>
        </div>

        <div
          v-if="
            sameCompanyReferralRequests &&
            sameCompanyReferralRequests.length > 0
          "
          class="mt-4 border-[1px] rounded-md p-4 bg-blue-50"
        >
          <h2>
            User has other applications at
            {{ applicationsStore.emailPreview.employer_details?.name }}. Do you want to include
            them?
          </h2>
          <ul class="flex flex-col gap-2 mt-2 ml-4">
            <li
              v-for="application in sameCompanyReferralRequests"
              :key="application.id"
              class="flex items-start gap-2"
            >
              <Checkbox
                :id="`app-${application.id}`"
                :checked="selectedApplicationIds.includes(application.id)"
                @click="() => handleCheckboxClick(application.id)"
                class="mt-0.5"
              />
              <label
                :for="`app-${application.id}`"
                class="cursor-pointer text-sm"
                @click="() => handleCheckboxClick(application.id)"
              >
                <a
                  :href="`/referral-requests/${application.id}`"
                  target="_blank"
                  class="inline-block hover:text-cosmicPurple transition-all duration-200 mr-2"
                  @click.stop
                  >{{ application.open_role.role_name }}</a
                >
                <a
                  :href="application.open_role.url"
                  target="_blank"
                  class="inline-block hover:text-cosmicPurple transition-all duration-200 mr-2"
                  @click.stop
                  ><SquareArrowOutUpRight class="w-3.5 h-3.5"
                /></a>
                <span class="inline-block text-sm text-gray-500"
                  >(last updated {{ formatDate(application.updated_at) }})</span
                >
              </label>
            </li>
          </ul>
        </div>

        <!-- Validation Warnings -->
        <div v-if="!applicationsStore.emailPreview.is_valid_email" class="mt-4">
          <div class="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div class="flex items-start">
              <AlertTriangle class="w-5 h-5 text-yellow-500 mt-0.5 mr-2" />
              <div>
                <h3 class="font-medium text-yellow-800">Missing Required Fields</h3>
                <ul class="mt-1 text-sm text-yellow-700 list-disc list-inside">
                  <li v-for="field in applicationsStore.emailPreview.missing_fields" :key="field">
                    {{ field }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Email Content -->
        <div class="border rounded-md p-4 bg-gray-50 mt-4 relative">
          <div class="font-semibold mb-2">Email Content</div>
          <!-- Loading overlay for content updates -->
          <div
            v-if="isUpdatingContent"
            class="absolute inset-0 bg-white/80 flex items-center justify-center rounded-md z-10"
          >
            <LoaderCircle class="animate-spin w-6 h-6 text-gray-500" />
          </div>
          <div
            v-html="applicationsStore.emailPreview.html_content"
            class="prose max-w-none email-content"
            :class="{ 'opacity-50': isUpdatingContent }"
          ></div>
        </div>
      </div>

      <!-- Error State -->
      <div
        v-if="applicationsStore.emailError"
        class="bg-red-50 border border-red-200 rounded-md p-4 mb-4"
      >
        <div class="flex">
          <AlertCircle class="w-5 h-5 text-red-500 mt-0.5 mr-2" />
          <div class="text-red-700">{{ applicationsStore.emailError }}</div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end gap-3 mt-6">
        <router-link
          :to="backRoute"
          class="px-4 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        >
          Cancel
        </router-link>
        <button
          @click="handleSendEmail"
          :disabled="
            !applicationsStore.emailPreview?.is_valid_email ||
            isSending ||
            isSent ||
            !!applicationsStore.emailError
          "
          :class="[
            'px-4 py-2 text-white rounded-md transition-all flex items-center gap-2 text-xs',
            isSent
              ? 'bg-green-500'
              : 'bg-cosmicPurple hover:bg-cosmicPurple/80 hover:translate-y-[-1px] hover:shadow-md'
          ]"
          :style="{
            opacity:
              !applicationsStore.emailPreview?.is_valid_email || !!applicationsStore.emailError
                ? '0.5'
                : '1'
          }"
        >
          <LoaderCircle v-if="isSending" class="animate-spin w-4 h-4" />
          <CheckCircle v-else-if="isSent" class="w-4 h-4" />
          {{ isSending ? 'Sending' : isSent ? 'Sent!' : 'Send Email' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import {
  AlertTriangle,
  AlertCircle,
  LoaderCircle,
  CheckCircle,
  Mail,
  SquareArrowOutUpRight,
  CircleAlert
} from 'lucide-vue-next'
import { useApplicationsStore } from '@/stores/applications'
import { useRoute, useRouter } from 'vue-router'
import { toast } from '@/components/ui/toast'
import { Checkbox } from '@/components/ui/checkbox'
import { formatDate } from '@/utils/formatDate'

const props = defineProps<{
  applicationId: string
  allApplications?: boolean
  showActions?: boolean
  isModal?: boolean
  onSuccess?: () => void
}>()

const emit = defineEmits<{
  (e: 'email-sent'): void
}>()

const applicationsStore = useApplicationsStore()
const route = useRoute()
const router = useRouter()

const isSending = ref(false)
const isSent = ref(false)
const selectedApplicationIds = ref<number[]>([])
const isUpdatingContent = ref(false)

// Only show applications that still need a referral (early stage statuses)
const REFERRAL_ELIGIBLE_STATUSES = ['NOT_STARTED', 'ONGOING']

// Filter same company applications to only show ones eligible for referral
const sameCompanyReferralRequests = computed(() => {
  const allApplications = applicationsStore.emailPreview?.same_company_applications || []
  return allApplications.filter(
    (app) => !app.application_status || REFERRAL_ELIGIBLE_STATUSES.includes(app.application_status)
  )
})

// Handle checkbox click
const handleCheckboxClick = async (id: number) => {
  // Toggle the selection manually
  const isCurrentlySelected = selectedApplicationIds.value.includes(id)

  if (isCurrentlySelected) {
    // Remove from selection
    const index = selectedApplicationIds.value.indexOf(id)
    selectedApplicationIds.value.splice(index, 1)
  } else {
    // Add to selection
    selectedApplicationIds.value.push(id)
  }

  // Only update the email content without full component re-render
  const mainId = route.params.application_id
    ? parseInt(route.params.application_id as string)
    : parseInt(props.applicationId)
  const allIds = [mainId, ...selectedApplicationIds.value]

  try {
    isUpdatingContent.value = true
    await applicationsStore.updateReferralEmailContent(allIds)
  } catch (error) {
    console.error('Failed to update email content:', error)
  } finally {
    isUpdatingContent.value = false
  }
}

// Watch for changes and log them
watch(
  selectedApplicationIds,
  (newIds) => {
    console.log('Selected application IDs:', newIds)
  },
  { deep: true }
)

const backRoute = computed(() => {
  if (props.allApplications) {
    const applicationId = route.params.application_id || props.applicationId
    return {
      name: 'All Applications Expanded Application',
      params: { application_id: applicationId },
      query: { ...route.query }
    }
  } else {
    // Check if we have role_id in the current route
    if (route.params.role_id) {
      const applicationId = route.params.application_id || props.applicationId
      return {
        name: 'Job Application container',
        params: {
          role_id: route.params.role_id,
          application_id: applicationId
        },
        query: { ...route.query }
      }
    } else {
      // When used in modal context, check if we have user id for applicant view
      if (route.params.id) {
        return {
          name: 'Applicant Profile with Role',
          params: {
            id: route.params.id,
            role_id: route.params.role_id || 'unknown'
          },
          query: { ...route.query }
        }
      } else {
        // Fallback to jobs list
        return { name: 'All jobs', query: { ...route.query } }
      }
    }
  }
})

const handleSendEmail = async () => {
  const mainApplicationId = route.params.application_id
    ? parseInt(route.params.application_id as string)
    : parseInt(props.applicationId)

  const applicationIds = [mainApplicationId, ...selectedApplicationIds.value]
  try {
    isSending.value = true
    await applicationsStore.sendReferralEmail(applicationIds)
    isSent.value = true
    toast({
      title: 'Success',
      description: 'Email sent successfully'
    })

    // Emit and optional callback
    emit('email-sent')
    if (props.onSuccess) {
      console.log('Calling onSuccess prop')
      props.onSuccess()
    }

    // If used inside modal, stop here
    if (props.isModal) {
      console.log('isModal is true, returning early')
      return
    }

    // Proceed with navigation
    console.log('isModal is false/undefined, proceeding with navigation')
    if (route.name === 'All Applications Expanded Referral email' || props.allApplications) {
      router.push(backRoute.value).catch(() => {})
    } else if (route.name === 'Job Application Referral email preview' || route.params.role_id) {
      router.push(backRoute.value).catch(() => {})
    } else if (
      route.name === 'Referral request details' ||
      String(route.path || '').startsWith('/referral-requests')
    ) {
      // Close and reopen to reset modal to first tab and refetch
      const requestId = (route.params.request_id as string) || props.applicationId
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      Promise.resolve()
        .then(() =>
          router.push({ name: 'Referral requests', query: { ...route.query } }).catch(() => {})
        )
        .then(() =>
          router
            .push({
              name: 'Referral request details',
              params: { request_id: requestId },
              query: { ...route.query }
            })
            .catch(() => {})
        )
    } else {
      router.push(backRoute.value).catch(() => {})
    }
  } catch (error) {
    console.error('Failed to send email:', error)
    toast({
      variant: 'destructive',
      title: 'Error',
      description: 'Failed to send email'
    })
  } finally {
    isSending.value = false
  }
}

// Get current application ID from route or props
const currentApplicationId = computed(() => {
  const routeId = route.params.application_id as string
  const propId = props.applicationId
  return routeId || propId
})

// Watch for applicationId changes and fetch preview
watch(
  currentApplicationId,
  async (newApplicationId) => {
    if (newApplicationId) {
      try {
        await applicationsStore.previewReferralEmail(newApplicationId)
      } catch (error) {
        console.error('Failed to preview email:', error)
      }
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.modal {
  max-height: 90vh;
}

.email-content :deep(ul) {
  list-style-type: disc !important;
  margin-left: 3rem !important;
  padding-left: 0 !important;
}

.email-content :deep(ol) {
  list-style-type: decimal !important;
  margin-left: 1.5rem !important;
  padding-left: 0 !important;
}

.email-content :deep(li) {
  display: list-item !important;
  margin-bottom: 0.25rem !important;
}

.email-content :deep(a) {
  color: #2563eb !important;
  text-decoration: underline !important;
}

.email-content :deep(a:hover) {
  color: #1d4ed8 !important;
}
</style>
